import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { api } from "@/lib/api";
import TrustBadges from "@/components/TrustBadges";
import { CHECKOUT } from "@/constants/testIds";

export default function CheckoutPage() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const { lang } = useLang();
  const t = useDict(lang);
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: user?.name || "", address: "", city: "", country: "", email: user?.email || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) navigate("/auth", { replace: true });
  }, [user, navigate]);

  if (!user) return null;

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await api.createCheckoutSession({
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        shipping_name: form.name, shipping_address: form.address, shipping_city: form.city,
        shipping_country: form.country, shipping_email: form.email,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe hosted checkout
      window.location.href = res.url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7">
        <h1 className="font-serif-display text-5xl mb-12">{t.checkout.title}</h1>
        <form onSubmit={pay} className="space-y-8">
          <div><label className="label-line">{t.checkout.name}</label><input data-testid={CHECKOUT.nameInput} required className="input-line" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
          <div><label className="label-line">{t.checkout.address}</label><input data-testid={CHECKOUT.addressInput} required className="input-line" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-6">
            <div><label className="label-line">{t.checkout.city}</label><input data-testid={CHECKOUT.cityInput} required className="input-line" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} /></div>
            <div><label className="label-line">{t.checkout.country}</label><input data-testid={CHECKOUT.countryInput} required className="input-line" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} /></div>
          </div>
          <div><label className="label-line">{t.checkout.email}</label><input data-testid={CHECKOUT.emailInput} required type="email" className="input-line" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
          {error && <p data-testid="checkout-error" className="text-sm text-[color:var(--red)]">{error}</p>}
          <button data-testid={CHECKOUT.submit} disabled={busy || items.length === 0} className="btn-navy mt-4">
            {busy ? "..." : (lang === 'fr' ? "Payer avec Stripe" : "Pay with Stripe")} →
          </button>
          <p className="text-xs text-[color:var(--ink-3)]">{lang === 'fr' ? 'Paiement sécurisé. Test card : 4242 4242 4242 4242' : 'Secure payment. Test card: 4242 4242 4242 4242'}</p>
          <div className="mt-6 pt-6 border-t border-[color:rgba(0,16,48,0.1)]">
            <TrustBadges variant="compact" />
          </div>
        </form>
      </div>
      <div className="lg:col-span-5 lg:pl-8 lg:border-l border-[color:rgba(0,16,48,0.1)]">
        <h2 className="eyebrow mb-6">{t.cart.subtotal}</h2>
        {items.map((i) => (
          <div key={i.product_id} className="flex gap-4 py-4 border-b border-[color:rgba(0,16,48,0.08)]">
            <img src={i.image} className="w-16 h-20 object-cover bg-[var(--bg-2)]" alt="" />
            <div className="flex-1">
              <p className="font-serif-display text-lg leading-tight">{i.name[lang] || i.name.fr}</p>
              <p className="text-xs text-[color:var(--ink-3)] mt-1">{i.qty} × {i.price.toFixed(2)} €</p>
            </div>
            <p className="font-serif-display text-lg">{(i.qty * i.price).toFixed(2)} €</p>
          </div>
        ))}
        <div className="mt-8 flex justify-between items-baseline">
          <span className="eyebrow">{t.cart.subtotal}</span>
          <span className="font-serif-display text-4xl">{subtotal.toFixed(2)} €</span>
        </div>
      </div>
    </div>
  );
}
